import { confirmToast } from "../../utils/toast";
import { sortDeals } from "../../utils/dealGridSort";
import React, { useEffect, useRef, useState } from "react";
import axios from "../../utils/axiosConfig";
import { useNavigate } from "react-router-dom";
import AdminClosedDealEditDialog from "../../components/AdminClosedDealEditDialog";
import FormattedDateInput from "../../components/FormattedDateInput";
import { formatReportDate } from "../../utils/reportDate";
import { DEFAULT_COLUMNS, normalizeColumns } from "../../utils/dealGridColumns";

export const getFinancialYearStartDate = (date = new Date()) => {
  const year = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  return `${year}-04-01`;
};

const DealMaster = () => {

  const navigate = useNavigate();
  const searchRequest = useRef(0);
  const dealStatusMap = {
    1: "New",
    2: "Cancel",
    3: "Closed",
    4: "Ongoing",
    5: "Claim"
  };

  const pendingStatusMap = {
    1: "Purchase Contract",
    2: "Sales Contract",
    3: "Advance Payment",
    4: "Shipment Schedule",
    5: "Document Pending",
    6: "Final Payment Pending",
    7: "Container Release"
  };
  const pendingRouteMap = {
    1: (id) => `/transaction/purchase-contract/${id}`,
    2: (id) => `/transaction/sales-contract/${id}`,
    3: (id) => `/transaction/advance-payment/${id}`,
    4: (id) => `/transaction/shipment-schedule/${id}`,
    5: (id) => `/transaction/shipment-schedule/${id}`,
    6: (id) => `/transaction/container-release/${id}`,
    7: (id) => `/transaction/container-release/${id}`
  };
  const getPendingStatusLabel = (deal) => (
    Number(deal.PendingStatus) === 4 && deal.ContainerNo
      ? "Container Detail Pending"
      : pendingStatusMap[deal.PendingStatus]
  );
  const getPriceModeLabel = (deal) => {
    const mode = String(deal.PriceMode || "").replace(/\s+/g, "").toLowerCase();
    if (mode === "fixed") return "Fixed";
    if (mode === "unfixed") return "Unfixed";
    if (deal.FixedAmount !== null && deal.FixedAmount !== undefined && (deal.UnFixedAmount === null || deal.UnFixedAmount === undefined)) return "Fixed";
    if (deal.UnFixedAmount !== null && deal.UnFixedAmount !== undefined && (deal.FixedAmount === null || deal.FixedAmount === undefined)) return "Unfixed";
    return "-";
  };
  const getPriceCurrencyLabel = (currency) => (
    String(currency || "").trim().toUpperCase() === "USD" ? "$" : currency || ""
  );
  const renderUnfixedStatus = (deal) => {
    const isFixed = getPriceModeLabel(deal) === "Fixed";
    const percentages = String(deal.UnfixedPercentages || "").split(",")
      .filter(value => value.trim() !== "" && Number.isFinite(Number(value)))
      .map(value => `${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%`);
    return <>
      <span className={`badge ${isFixed ? "bg-success" : "bg-warning text-dark"}`}>
        {isFixed ? "Fixed" : "Unfixed"}
      </span>
      {isFixed && percentages.length > 0 && ` (${percentages.join(", ")})`}
    </>;
  };
  const renderContainerPrices = (deal) => {
    const prices = deal.ContainerFixedPrices;
    const fixedPrices = [...new Set(prices.filter(price => price != null).map(Number))];
    const hasUnfixed = prices.some(price => price == null);
    return <>
      {fixedPrices.length > 0 && `${getPriceCurrencyLabel(deal.DealCurrency)} ${fixedPrices
        .map(price => price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })).join(", ")}`}
      {hasUnfixed && <>
        {fixedPrices.length > 0 && ", "}
        <span className="badge bg-warning text-dark">Unfixed</span>
      </>}
    </>;
  };
  const handleEditRedirect = (deal) => {

    // 🚫 If deal is closed, block editing
    if (Number(deal.DealStatus) === 2) {
      alert(`This deal is ${dealStatusMap[Number(deal.DealStatus)]} and cannot be edited.`);
      return;
    }
    if (Number(deal.DealStatus) === 3) {
      setClosedDealForApproval(deal);
      return;
    }

    const routeBuilder = pendingRouteMap[deal.PendingStatus];

    if (routeBuilder) {
      navigate(routeBuilder(deal.DealID));
    } else {
      // fallback
      navigate(`/transaction/new-deal/edit/${deal.DealID}`);
    }
  };
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [closedDealForApproval, setClosedDealForApproval] = useState(null);
  const [containerDetails, setContainerDetails] = useState([]);
  const fetchContainerDetails = async (dealId) => {
    try {
      const res = await axios.get(`/newdeal/${dealId}/containers`);
      setContainerDetails(res.data);
      setSelectedDealId(dealId);
    } catch (err) {
      console.error("Failed to load container details:", err);
    }
  };
  const today = new Date();

  const [filters, setFilters] = useState({
    fromDate: getFinancialYearStartDate(today),
    toDate: formatDate(today),
    shipperId: "", buyerId: "", status: "", pendingStatus: "", priceMode: "",
    dealId: "", containerNo: "", invoiceNo: "", blNo: "", salesContractNo: ""
  });

  const [deals, setDeals] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState({ key: "", direction: "asc" });
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);
  const renderGridColumns = fragment => {
    const cells = React.Children.toArray(fragment.props.children);
    return visibleColumns.map(key => React.cloneElement(cells[DEFAULT_COLUMNS.indexOf(key)], { key }));
  };
  useEffect(() => {
    let active = true;
    axios.get("/newdeal/grid-settings").then(res => {
      if (active) setVisibleColumns(normalizeColumns(res.data.columns));
    }).catch(() => { /* Keep the standard layout when preferences cannot be loaded. */ });
    return () => { active = false; };
  }, []);
  const sortHeader = (key, label) => (
    <th aria-sort={sort.key === key ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className="btn btn-link p-0 text-start text-decoration-none fw-bold"
        style={{ color: "inherit" }}
        onClick={() => {
          setSort(current => ({ key, direction: current.key === key && current.direction === "asc" ? "desc" : "asc" }));
          setCurrentPage(1);
        }}>
        {label} <span aria-hidden="true">{sort.key === key ? (sort.direction === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
  const rowsPerPage = 20;
  const [buyers, setBuyers] = useState([]);
  const [shippers, setShippers] = useState([]);

  useEffect(() => {
    loadBuyers();
    loadShippers();
  }, []);
  useEffect(() => {
    handleSearch();
    // Load the same date range shown in the filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (!await confirmToast("Delete this deal?")) return;
    await axios.delete(`/newdeal/${id}`);
    handleSearch();
  };
  const loadBuyers = async () => {
    try {
      const res = await axios.get(`${"/newdeal"}/buyers`);
      setBuyers(res.data);
    } catch (err) {
      console.error(err);
    }
  };
  const loadShippers = async () => {
    try {
      const res = await axios.get(`${"/newdeal"}/shippers`);
      setShippers(res.data);
    } catch (err) {
      console.error(err);
    }
  };
  // Pagination
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentData = sortDeals(deals, sort.key, sort.direction).slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(deals.length / rowsPerPage);
  const handleSearch = async (searchFilters = filters) => {
    const requestId = ++searchRequest.current;
    if (searchFilters.fromDate && searchFilters.toDate && searchFilters.fromDate > searchFilters.toDate) {
      alert("From date cannot be after To date");
      return;
    }
    // Convert status and pendingStatus to numbers if present
    const statusValue = searchFilters.status ? Number(searchFilters.status) : "";
    const pendingStatusValue = searchFilters.pendingStatus ? Number(searchFilters.pendingStatus) : "";

    const payload = {
      ...searchFilters,
      status: statusValue,
      pendingStatus: pendingStatusValue,
      shipperId: searchFilters.shipperId ? Number(searchFilters.shipperId) : null,
      buyerId: searchFilters.buyerId ? Number(searchFilters.buyerId) : null,
      dealId: searchFilters.dealId ? searchFilters.dealId.trim() : null,
    };

    try {
      const res = await axios.post("/newdeal/search", payload);
      if (requestId !== searchRequest.current) return;
      setSelectedDealId(null);
      setContainerDetails([]);
      setDeals(res.data);
      setCurrentPage(1);
    } catch (err) {
      if (requestId !== searchRequest.current) return;
      alert("Search failed: " + (err.response?.data?.message || err.response?.data || err.message));
    }
  };

  const handleClear = async () => {
    const empty = {
      fromDate: "",
      toDate: "",
      shipperId: "",
      buyerId: "",
      status: "",
      pendingStatus: "",
      priceMode: "",
      dealId: "",
      containerNo: "",
      invoiceNo: "", blNo: "", salesContractNo: ""
    };

    setFilters(empty);

    await handleSearch(empty);
  };
  return (
    <>
    <div className="container-fluid mt-3">

      <div className="card shadow-sm border-0">

        {/* Header */}
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Deal List</h5>
          <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate("/tools/grid-settings")}>Column Settings</button>
          <button
            className="btn btn-primary"
            onClick={() => navigate("/transaction/new-deal/add")}
          >
            + Add New Deal
          </button>
          </div>
        </div>

        <div className="card-body">

          {/* FILTER PANEL */}
          <div className="row g-3 mb-3">

            <div className="col-md-2">
              <label htmlFor="dealFromDate">From</label>
              <FormattedDateInput
                id="dealFromDate"
                name="fromDate"
                value={filters.fromDate}
                onChange={(e) =>
                  setFilters({ ...filters, fromDate: e.target.value })}
              />
            </div>

            <div className="col-md-2">
              <label htmlFor="dealToDate">To</label>
              <FormattedDateInput
                id="dealToDate"
                name="toDate"
                value={filters.toDate}
                onChange={(e) =>
                  setFilters({ ...filters, toDate: e.target.value })}
              />
            </div>

            <div className="col-md-2">
              <label>Supplier</label>
              <select
                className="form-select"
                value={filters.shipperId}
                onChange={(e) =>
                  setFilters({ ...filters, shipperId: e.target.value })}
              >
                <option value="">All</option>
                {shippers.map((ship) => (
                  <option key={ship.ShipperID} value={ship.ShipperID}>
                    {ship.ShipperName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label>Buyer</label>
              <select
                className="form-select"
                value={filters.buyerId}
                onChange={(e) =>
                  setFilters({ ...filters, buyerId: e.target.value })}
              >
                <option value="">All</option>
                {buyers.map((buyer) => (
                  <option key={buyer.BuyerID} value={buyer.BuyerID}>
                    {buyer.BuyerName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <label>Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => {
                  const next = { ...filters, status: e.target.value };
                  setFilters(next);
                  handleSearch(next);
                }}
              >
                <option value="">All</option>
                <option value="1">New</option>
                <option value="4">Ongoing</option>
                <option value="2">Cancel</option>
                <option value="3">Closed</option>
                <option value="5">Claim</option>
              </select>
            </div>

            <div className="col-md-2">
              <label>Pending Status</label>
              <select
                className="form-select"
                value={filters.pendingStatus}
                onChange={(e) => {
                  const next = { ...filters, pendingStatus: e.target.value };
                  setFilters(next);
                  handleSearch(next);
                }}
              >
                <option value="">All</option>
                <option value="1">Purchase Contract</option>
                <option value="2">Sales Contract</option>
                <option value="3">Advance Payment</option>
                <option value="4">Shipment Schedule</option>
                <option value="5">Document Pending</option>
                <option value="6">Final Payment Pending</option>
                <option value="7">Container Release</option>
              </select>
            </div>

            <div className="col-md-2">
              <label>Price Status</label>
              <select
                className="form-select"
                value={filters.priceMode}
                onChange={(e) => {
                  const next = { ...filters, priceMode: e.target.value };
                  setFilters(next);
                  handleSearch(next);
                }}
              >
                <option value="">All</option>
                <option value="Fixed">Fixed</option>
                <option value="UnFixed">Unfixed</option>
              </select>
            </div>

            <div className="col-md-2">
              <label htmlFor="dealId">Deal ID</label>
              <input
                id="dealId"
                type="text"
                className="form-control"
                value={filters.dealId}
                onChange={(e) =>
                  setFilters({ ...filters, dealId: e.target.value })}
              />
            </div>

            <div className="col-md-2">
              <label htmlFor="containerNo">Container No</label>
              <input
                id="containerNo"
                type="text"
                className="form-control"
                value={filters.containerNo}
                onChange={(e) =>
                  setFilters({ ...filters, containerNo: e.target.value })}
              />
            </div>

            <div className="col-md-2">
              <label htmlFor="invoiceNo">Invoice No</label>
              <input
                id="invoiceNo"
                type="text"
                className="form-control"
                value={filters.invoiceNo}
                onChange={(e) =>
                  setFilters({ ...filters, invoiceNo: e.target.value })}
              />
            </div>

            {[["blNo", "BL No"], ["salesContractNo", "Sales Contract No"]].map(([name, label]) => (
              <div className="col-md-2" key={name}>
                <label htmlFor={name}>{label}</label>
                <input id={name} type="text" className="form-control" value={filters[name]}
                  onChange={e => setFilters({ ...filters, [name]: e.target.value })} />
              </div>
            ))}

            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-success w-100 me-2"
                onClick={() => handleSearch()}
              >
                Search
              </button>
            </div>

            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-secondary w-100"
                onClick={handleClear}
              >
                Clear
              </button>
            </div>

          </div>

          {/* GRID */}
          <div className="table-responsive">
            <table className="table table-bordered table-hover">
              <thead className="table-light">
                <tr>
                  {renderGridColumns(<>
                  <th scope="col" className="text-nowrap">Sr No</th>
                  {sortHeader("DealID", "Deal Id")}
                  {sortHeader("DealDate", "Deal Date")}
                  {sortHeader("ShipperName", "Shipper Name")}
                  {sortHeader("BuyerName", "Buyer Name")}
                  {sortHeader("NoOfContainer", <>No Of<br />Container</>)}
                  {sortHeader("ItemNames", "Item")}
                  {sortHeader("Price", "Price Status")}
                  {sortHeader("TotalQty", "Total Qty")}
                  {sortHeader("DealStatus", "Status")}
                  {sortHeader("PendingStatus", "Pending Status")}
                  <th>Action</th>
                  </>)}
                </tr>
              </thead>
              <tbody>
                {currentData.map((d, index) => (
                  <tr
                    key={d.DealID}
                    style={{ cursor: "pointer" }}
                    onClick={() => fetchContainerDetails(d.DealID)}
                  >
                    {renderGridColumns(<>
                    <td>{indexOfFirst + index + 1}</td>
                    <td>{d.DealNo || d.DealID}</td>
                    <td>
                      {formatReportDate(d.DealDate, "")}
                    </td>
                    <td>{d.ShipperName}</td>
                    <td>{d.BuyerName}</td>
                    <td>{d.NoOfContainer}</td>
                    <td>{d.ItemNames || ""}</td>
                    <td>
                      {Number(d.OriginallyUnfixed) === 1 || getPriceModeLabel(d) === "Unfixed"
                        ? renderUnfixedStatus(d)
                        : d.ContainerFixedPrices?.length
                        ? renderContainerPrices(d)
                        : d.FixedAmount !== null && d.FixedAmount !== undefined && d.FixedAmount !== ""
                        ? `${getPriceCurrencyLabel(d.DealCurrency)} ${Number(d.FixedAmount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : <span className="badge bg-warning text-dark">{getPriceModeLabel(d)}</span>}
                    </td>
                    <td>{d.TotalQty !== null && d.TotalQty !== undefined && d.TotalQty !== ""
                      ? Number(d.TotalQty).toLocaleString("en-US", { maximumFractionDigits: 0, useGrouping: false })
                      : ""}</td>
                    <td>
                      {d.DealStatus !== undefined && d.DealStatus !== null && dealStatusMap[Number(d.DealStatus)] ? (
                        <span className={`badge ${Number(d.DealStatus) === 3 ? "bg-dark" : Number(d.DealStatus) === 2 ? "bg-danger" : Number(d.DealStatus) === 5 ? "bg-warning text-dark" : "bg-success"}`}>
                          {dealStatusMap[Number(d.DealStatus)]}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {d.PendingStatus !== undefined && d.PendingStatus !== null && pendingStatusMap[Number(d.PendingStatus)] ? (
                        <span className="badge bg-warning text-dark">
                          {getPendingStatusLabel(d)}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditRedirect(d);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(d.DealID)}
                      >
                        Delete
                      </button>
                    </td>
                    </>)}
                  </tr>
                ))}
              </tbody>
            </table>
            {selectedDealId && (
              <div className="mt-4">
                <div className="card shadow-sm">
                  <div className="card-header bg-light">
                    <strong>Container Details</strong>
                  </div>

                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-bordered table-sm mb-0">
                        <thead className="table-secondary">
                          <tr>
                            <th>Container ID</th>
                            <th>Size</th>
                            <th>Port Loading</th>
                            <th>Port Discharge</th>
                            <th>Delivery Port</th>
                            <th>Create Date</th>

                            {/* Temporary columns */}
                            <th>Container No</th>
                            <th>Shipping Co</th>
                            <th>Invoice No</th>
                            <th>BL No</th>
                            <th>Status</th>
                          </tr>
                        </thead>

                        <tbody>
                          {containerDetails.length > 0 ? (
                            containerDetails.map((c) => (
                              <tr key={c.ContainerID}>
                                <td>{c.ContainerID}</td>
                                <td>{c.Size}</td>
                                <td>{c.PortLoading}</td>
                                <td>{c.PortDischarge}</td>
                                <td>{c.DeliveryPort}</td>
                                <td>
                                  {formatReportDate(c.CreateDate, "")}
                                </td>

                                <td>{c.ContainerNo || "-"}</td>
                                <td>{c.ShippingCo || "-"}</td>
                                <td>{c.InvoiceNo || "-"}</td>
                                <td>{c.BLNo || "-"}</td>
                                <td>{c.Status || "-"}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="11" className="text-center">
                                No container records found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* PAGINATION */}
          <div className="text-muted mt-3" aria-live="polite">
            Showing {deals.length ? indexOfFirst + 1 : 0}–{Math.min(indexOfLast, deals.length)} of {deals.length} deals
          </div>
          {totalPages > 1 && (
            <ul className="pagination mt-3 flex-wrap" aria-label="Deal pages">
              {[...Array(totalPages)].map((_, i) => (
                <li
                  key={i}
                  className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    aria-current={currentPage === i + 1 ? "page" : undefined}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                </li>
              ))}
            </ul>
          )}

        </div>
      </div>
    </div>
    <AdminClosedDealEditDialog
      deal={closedDealForApproval}
      onCancel={() => setClosedDealForApproval(null)}
      onApproved={(approvalToken) => {
        const dealId = closedDealForApproval.DealID;
        sessionStorage.setItem(`closedDealEditApproval:${dealId}`, approvalToken);
        setClosedDealForApproval(null);
        navigate(`/transaction/new-deal/edit/${dealId}`);
      }}
    />
    </>
  );
};



export default DealMaster;

